import React from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import * as Yup from "yup";
import Cookies from "universal-cookie";
import moment from "moment-timezone";

import { useMessages } from "providers/BrandingProvider";
import * as utils from "commons/utils";
import * as Constants from "commons/constants";
import { FormTextField, FormCheckbox } from "components/forms/FormikFields";
import {
  ModalForm,
  ModalFormFieldset,
  ModalFormLinkButtonset,
  ModalSubmitButton,
} from "../common/FormComponents";
import ModalMessages from "../common/ModalMessages";
import { LinkButton } from "components/Controls";
import { login } from "services/AuthService";

import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import AccountCircleOutlinedIcon from "@material-ui/icons/AccountCircleOutlined";
//import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
//import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined';

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  adornment: {
    fontSize: "1.0rem",
    color: theme.palette.primary.main,
  },
  checkboxLabelRoot: {
    margin: "6px 0",
  },
  checkboxLabel: {
    marginLeft: 4,
    fontSize: "0.7rem",
  },
}));

const cookies = new Cookies();

const LoginForm = ({ onSuccess, onForgetPasswordClick, onNewAccountClick }) => {
  const classes = useStyles();
  const Messages = useMessages();

  const savedRememberValue = cookies.get("rememberme");
  const savedUsername =
    utils.hasNonEmptyValue(savedRememberValue) && savedRememberValue !== "false" ? savedRememberValue : "";
  const [remember, setRemember] = React.useState(savedRememberValue !== "false");
  const [errorMessages, setErrorMessages] = React.useState([]);

  const LoginSchema = Yup.object().shape({
    username: Yup.string().required(Messages.MESSAGE.VALIDATION.REQUIRED),
    password: Yup.string().required(Messages.MESSAGE.VALIDATION.REQUIRED),
    remember: Yup.string(),
  });

  React.useEffect(() => {
    setErrorMessages([]);
  }, []);

  const handleRememberMeChange = (e, value) => {
    setRemember(value);
  };

  return (
    <React.Fragment>
      <ModalMessages messages={errorMessages} level={Constants.ALERT_LEVEL.ERROR} />
      <Formik
        initialValues={{ username: savedUsername, password: "", remember: remember }}
        validationSchema={LoginSchema}
        onSubmit={(values) => {
          setErrorMessages([]);

          login({ username: values.username, password: values.password })
            .then((response) => {
              if (utils.hasNonEmptyValue(response)) {
                const cookieValue = remember ? values.username : "false";
                const oneyear = moment().add(1, "years");
                cookies.set("rememberme", cookieValue, {
                  path: "/",
                  expires: oneyear.toDate(),
                  sameSite: "strict",
                });
                onSuccess();
              } else {
                setErrorMessages([Messages.MESSAGE.AUTHENTICATION_FAIL]);
              }
            })
            .catch((error) => {
              setErrorMessages([Messages.MESSAGE.GENERAL_RETRY]);
            });
        }}
      >
        {({ errors, touched }) => (
          <ModalForm>
            <ModalFormFieldset>
              <FormTextField
                placeholder={Messages.FORM_LABEL.USERNAME}
                name="username"
                autoFocus={true}
                startAdornment={<AccountCircleOutlinedIcon className={classes.adornment} />}
              />
              <FormTextField
                placeholder={Messages.FORM_LABEL.PASSWORD}
                name="password"
                type="password"
                startAdornment={<VpnKeyOutlinedIcon className={classes.adornment} />}
              />
              <div style={{ textAlign: "center" }}>
                <FormCheckbox
                  label="Remember Me"
                  classes={{ root: classes.checkboxLabelRoot, label: classes.checkboxLabel }}
                  field={{ value: remember, name: "remember", onChange: handleRememberMeChange }}
                />
              </div>
            </ModalFormFieldset>
            {(onForgetPasswordClick || onNewAccountClick) && (
              <ModalFormLinkButtonset>
                {onForgetPasswordClick && (
                  <LinkButton
                    onClick={onForgetPasswordClick}
                    variant="form"
                    label={Messages.LABEL.FORGOT_PASSWORD}
                  />
                )}
                {onNewAccountClick && (
                  <LinkButton
                    onClick={onNewAccountClick}
                    variant="form"
                    label={Messages.LABEL.NEED_ACCOUNT}
                  />
                )}
              </ModalFormLinkButtonset>
            )}
            <ModalSubmitButton label={Messages.LABEL.LOGIN} />
          </ModalForm>
        )}
      </Formik>
    </React.Fragment>
  );
};

LoginForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onForgetPasswordClick: PropTypes.func,
  onNewAccountClick: PropTypes.func,
};

export default LoginForm;
